const DOCUMENT_VERSION = 1;

export const PrinterDocumentType = Object.freeze({
  INVOICE: 'invoice',
  SUMMARY: 'summary',
  TEST: 'test',
});

const toNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const buildBusiness = (business = {}) => ({
  id: business.id || business.orgId || null,
  name: business.name || business.businessName || 'Your Business',
  phone: business.phone || business.contactNumber || null,
  email: business.email || business.contactEmail || null,
  address: business.address || business.registeredAddress || null,
  taxId: business.taxId || business.gstNumber || business.gst || null,
  logoUrl: business.logoUrl || business.logoDataUrl || null,
  meta: {
    ...business.meta,
  },
});

const buildCustomer = (customer = {}) => ({
  id: customer.customerId || customer.id || null,
  name: customer.name || customer.customerName || 'Customer',
  phone: customer.phone || customer.customerPhone || null,
  email: customer.email || customer.customerEmail || null,
  address: customer.address || customer.customerAddress || null,
  taxId: customer.taxId || customer.gstNumber || null,
  meta: {
    customerNumber: customer.customerNumber || null,
    ...customer.meta,
  },
});

const normalizeItems = (items = []) => {
  return items.map((item, index) => {
    const quantity = toNumber(item.quantity, 0);
    const unitPrice = toNumber(item.price ?? item.unitPrice, 0);
    const tax = toNumber(item.tax ?? item.taxAmount, 0);
    
    // Calculate discount from discountPercent if provided, otherwise use discount/discountAmount
    const discountPercent = toNumber(item.discountPercent, 0);
    const baseTotal = quantity * unitPrice;
    let discount = toNumber(item.discount ?? item.discountAmount, 0);
    
    // If discountPercent is provided and discount is 0, calculate from percentage
    if (discountPercent > 0 && discount === 0) {
      discount = (baseTotal * discountPercent) / 100;
    }
    
    const lineTotal = item.total != null
      ? toNumber(item.total)
      : toNumber(item.lineTotal) || (baseTotal + tax - discount);

    return {
      id: item.id || item.itemId || item.productId || `item-${index}`,
      name: item.name || 'Item',
      quantity,
      unitPrice,
      unit: item.unit || item.unitName || null,
      tax,
      discount,
      discountPercent,
      lineTotal,
      originalTotal: baseTotal, // Store original total before discount for display
      notes: item.notes || null,
      meta: {
        productId: item.productId || item.id || null,
        sku: item.sku || null,
        hsn: item.hsn || null,
        ...item.meta,
      },
    };
  });
};

const buildPayment = (payment = {}) => ({
  method: payment.method || payment.mode || null,
  amount: toNumber(payment.amount ?? payment.paid, 0),
  reference: payment.reference || payment.referenceId || null,
  status: payment.status || null,
  meta: {
    ...payment.meta,
  },
});

const deriveTotals = (normalizedItems, totals = {}, payment = {}) => {
  const subTotal = normalizedItems.reduce((acc, item) => acc + (item.lineTotal - item.tax + item.discount), 0);
  const lineSum = normalizedItems.reduce((acc, item) => acc + item.lineTotal, 0);
  const taxTotal = totals.tax != null ? toNumber(totals.tax) : normalizedItems.reduce((acc, item) => acc + item.tax, 0);
  const discountTotal = totals.discount != null ? toNumber(totals.discount) : normalizedItems.reduce((acc, item) => acc + item.discount, 0);
  const serviceCharge = toNumber(totals.serviceCharge, 0);
  const grandTotal = totals.grandTotal != null
    ? toNumber(totals.grandTotal)
    : lineSum + serviceCharge;
  const paid = totals.paid != null ? toNumber(totals.paid) : toNumber(payment.amount, 0);
  const balanceDue = totals.balanceDue != null ? toNumber(totals.balanceDue) : grandTotal - paid;

  return {
    subTotal,
    lineTotal: lineSum,
    tax: taxTotal,
    discount: discountTotal,
    serviceCharge,
    grandTotal,
    paid,
    balanceDue,
  };
};

// Helper to convert Firestore timestamp to ISO string
const toDateString = (value) => {
  if (!value) return new Date().toISOString();
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'string') return value;
  if (typeof value.toDate === 'function') {
    try {
      return value.toDate().toISOString();
    } catch {
      return new Date().toISOString();
    }
  }
  if (value && typeof value === 'object' && '_seconds' in value) {
    return new Date(value._seconds * 1000).toISOString();
  }
  if (value && typeof value === 'object' && 'seconds' in value) {
    return new Date(value.seconds * 1000).toISOString();
  }
  return new Date(value).toISOString();
};

export const buildPrintableInvoice = ({
  invoiceNumber,
  business,
  customer,
  items,
  totals,
  payment,
  currency = 'INR',
  metadata = {},
}) => {
  const normalizedItems = normalizeItems(items);
  const derivedTotals = deriveTotals(normalizedItems, totals, payment);

  return {
    version: DOCUMENT_VERSION,
    type: PrinterDocumentType.INVOICE,
    currency,
    invoiceNumber: invoiceNumber || metadata.invoiceNumber || metadata.billName || null,
    issueDate: toDateString(metadata.issueDate || metadata.createdAt || new Date()),
    business: buildBusiness(business),
    customer: buildCustomer(customer),
    items: normalizedItems,
    totals: derivedTotals,
    payment: buildPayment(payment),
    metadata: {
      source: metadata.source || null,
      orderId: metadata.orderId || null,
      draftId: metadata.draftId || null,
      accountId: metadata.accountId || null,
      moneyAccountId: metadata.moneyAccountId || null,
      offline: metadata.offline ?? false,
      createdAt: metadata.createdAt || null,
      updatedAt: metadata.updatedAt || null,
      createdBy: metadata.createdBy || null,
      notes: metadata.notes || null,
      tags: metadata.tags || [],
      extras: metadata.extras || {},
      footerText: metadata.footerText || business?.footerText || null, // Include footer text from business settings
    },
  };
};

export const buildPrintableInvoiceFromOrder = (order = {}, org = {}, options = {}) => {
  const items = Array.isArray(order.items) ? order.items : [];
  const payment = {
    method: order.paymentMethod || order.paymentMode,
    amount: order.amountPaid,
    reference: order.paymentReference,
    status: order.paymentStatus,
  };

  return buildPrintableInvoice({
    invoiceNumber: order.name || order.billName || order.invoiceNumber,
    business: org || {},
    customer: {
      customerId: order.customerId,
      name: order.customerName,
      phone: order.customerPhone,
      email: order.customerEmail,
      address: order.customerAddress,
      customerNumber: order.customerNumber,
    },
    items,
    totals: {
      subTotal: order.subTotal || (items.reduce((sum, item) => sum + ((item.price || item.unitPrice || 0) * (item.quantity || 0)), 0)),
      discount: order.discount || order.discountTotal || 0,
      tax: order.taxTotal,
      serviceCharge: order.serviceChargeTotal,
      grandTotal: order.sellingPrice ?? order.total,
      paid: order.amountPaid,
      balanceDue: order.balanceDue,
    },
    payment: options.payment || payment,
    currency: order.currency || 'INR',
    metadata: {
      source: order.source || 'orders',
      orderId: order.orderId,
      accountId: order.accountId,
      moneyAccountId: order.money_account_id || order.moneyAccountId,
      offline: order.syncStatus === 'PENDING_CREATE',
      // Use createdDate (has actual time) or createdAt, fallback to date or current time
      createdAt: order.createdDate || order.createdAt || order.date || order.created_on || new Date(),
      updatedAt: order.updatedAt || order.updatedDate || order.updated_on,
      createdBy: order.createdBy,
      notes: order.notes || null,
      tags: order.tags || [],
      extras: options.metadata?.extras || {},
      footerText: org?.footerText || options.metadata?.footerText || null, // Include footer text from org settings
      ...options.metadata,
    },
  });
};

export const buildPrintableInvoiceFromDraft = ({
  billName,
  billItems = [],
  customer = {},
  business = {},
  totals = {},
  metadata = {},
  payment = {},
  currency = 'INR',
}) => {
  const isNavigatorAvailable = typeof navigator !== 'undefined' && navigator && typeof navigator.onLine === 'boolean';
  const isOnline = isNavigatorAvailable ? navigator.onLine : true;

  const derivedTotals = {
    grandTotal: totals.grandTotal,
    discount: totals.discount,
    tax: totals.tax,
    serviceCharge: totals.serviceCharge,
    paid: totals.paid,
    balanceDue: totals.balanceDue,
  };

  return buildPrintableInvoice({
    invoiceNumber: billName || metadata.invoiceNumber,
    business,
    customer,
    items: billItems,
    totals: derivedTotals,
    payment,
    currency,
    metadata: {
      source: metadata.source || 'fast-billing',
      draftId: metadata.draftId || null,
      accountId: metadata.accountId || null,
      moneyAccountId: metadata.moneyAccountId || null,
      offline: metadata.offline ?? !isOnline,
      createdAt: metadata.createdAt || new Date().toISOString(),
      createdBy: metadata.createdBy || null,
      notes: metadata.notes || null,
      tags: metadata.tags || [],
      extras: metadata.extras || {},
      footerText: business?.footerText || metadata.footerText || null, // Include footer text from business settings
    },
  });
};

export const printableInvoiceToLegacyInvoiceData = (printableInvoice) => {
  if (!printableInvoice) {
    return null;
  }

  return {
    orderId: printableInvoice.metadata?.orderId || null,
    billName: printableInvoice.invoiceNumber || 'Invoice',
    customerName: printableInvoice.customer?.name || 'Customer',
    customerPhone: printableInvoice.customer?.phone || '',
    items: (printableInvoice.items || []).map((item) => ({
      name: item.name,
      price: item.unitPrice,
      quantity: item.quantity,
      unit: item.unit,
      total: item.lineTotal,
      discountPercent: item.discountPercent || 0,
      discount: item.discount || 0,
      originalTotal: item.originalTotal || (item.unitPrice * item.quantity),
    })),
    total: printableInvoice.totals?.grandTotal ?? 0,
    subtotal: printableInvoice.totals?.subTotal ?? printableInvoice.totals?.lineTotal ?? 0,
    discount: printableInvoice.totals?.discount ?? 0,
    currency: printableInvoice.currency,
  };
};

export const extractBusinessFromOrg = (org = {}) => buildBusiness(org);

